import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * App-standard searchable dropdown (Command + Popover combobox), matching the
 * pickers used elsewhere (e.g. ActivityComparison's employee picker).
 *
 * - Type to filter; the list scrolls and shows ~5 rows at a time.
 * - `allowCustom` lets the user commit whatever they typed as a new value
 *   (used by the Add Asset category field, which can add a brand-new category).
 */
export interface SearchableComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  allowCustom?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Pin the panel to the trigger's width instead of letting it shrink-wrap to
   * its widest option. Long labels then wrap onto the next line rather than
   * widening the panel — what you want in a filter bar, where a runaway panel
   * covers the table behind it.
   */
  matchTriggerWidth?: boolean;
  /**
   * Extra classes for the dropdown panel. The panel is portalled to <body> at
   * z-[200], so a combobox inside a higher overlay (e.g. a z-[1000] custom
   * modal) must raise it here or the list opens hidden behind the modal.
   */
  contentClassName?: string;
}

export function SearchableCombobox({
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No match.",
  allowCustom = false,
  disabled = false,
  className,
  matchTriggerWidth = false,
  contentClassName,
}: SearchableComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const trimmed = query.trim();
  // Show a "use typed value" affordance only when custom entry is allowed and
  // what they typed isn't already an option (case-insensitive).
  const showCreate =
    allowCustom &&
    !!trimmed &&
    !options.some((o) => o.toLowerCase() === trimmed.toLowerCase());

  const commit = (v: string) => {
    onChange(v);
    setQuery("");
    setOpen(false);
  };

  // cmdk filters by the item `value`; keep its built-in fuzzy match.
  const items = useMemo(() => options, [options]);

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          // The trigger has to fit its form cell, so a long pick still clips —
          // hovering shows it in full.
          title={value || undefined}
          // Match the themed native inputs/selects this sits beside — those get
          // `--gt-radius-input` from .gt-themed, which never applies to a
          // <button>, so it would otherwise fall back to a flatter rounded-md.
          style={{ borderRadius: "var(--gt-radius-input, 0.375rem)" }}
          className={cn(
            "w-full justify-between font-normal bg-white",
            !value && "text-gray-400",
            className
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* The trigger can be half a form row wide, but option labels (e.g. the
          depreciation-schedule asset categories) are much longer — so the panel
          shrink-wraps to its widest option instead of the trigger, never going
          narrower than the trigger nor wider than the cap below.
          The cap is a plain length on purpose: a `var(--radix-…)` inside min()
          resolves at computed-value time, and if Radix hasn't set the var yet the
          whole max-width is dropped and `max-content` runs away (a 100-char option
          then blew the panel past its dialog). Callers must keep their dialog
          wider than this, or the panel spills outside it. */}
      <PopoverContent
        className={cn("p-0", contentClassName)}
        align="start"
        style={
          matchTriggerWidth
            ? {
                width: "var(--radix-popover-trigger-width)",
                maxWidth: "var(--radix-popover-trigger-width)",
              }
            : {
                width: "max-content",
                minWidth: "var(--radix-popover-trigger-width)",
                maxWidth: "min(32rem, calc(100vw - 3rem))",
              }
        }
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            className="text-sm"
          />
          {/* ~5 rows tall (each ~2.25rem), then scrolls. When this combobox is
              inside a modal Dialog, the Dialog's scroll-lock (react-remove-scroll)
              cancels wheel events that bubble to `document`; stop them here so the
              list scrolls on wheel/touch, not just by dragging the scrollbar. */}
          <CommandList
            className="max-h-[11.25rem]"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty className="py-3 text-center text-xs text-gray-400">
              {emptyText}
            </CommandEmpty>
            {showCreate && (
              <CommandGroup>
                <CommandItem
                  value={`__create__${trimmed}`}
                  onSelect={() => commit(trimmed)}
                  className="cursor-pointer text-sm"
                >
                  <span className="min-w-0 flex-1 whitespace-normal break-words text-emerald-600">
                    + Add “{trimmed}”
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {items.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => commit(opt)}
                  className="cursor-pointer items-start text-sm"
                >
                  <Check
                    className={cn(
                      "mr-2 mt-0.5 h-4 w-4",
                      value === opt ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {/* Wrap rather than clip: the whole category name is what
                      distinguishes one option from the next. `min-w-0` lets the
                      span shrink below its content width (flex children default
                      to min-width:auto), which is what makes wrapping kick in. */}
                  <span className="min-w-0 flex-1 whitespace-normal break-words">
                    {opt}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
